import { SearchResult } from "#next/utils/interfaces";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { TUserSearchHit } from "#next/server/elasticSearch";
import {
  SocialAnalyzerResponse,
  SherlockResponse,
} from "#next/server/routers/userSearch";
// import { TUserNetwork } from "#next/server/routers/userSearch";

export type SuggestedProfileResult = {
  username?: string;
  platform?: string; // platform should not be optional, this is necessary due to the way we have to parse the response from elasticsearch
  count: number;
};

export type BreachSearchResult = {
  count: number;
  lines: string[];
};

export interface UserSearchUIState {
  profileSearchResult: TUserSearchHit | undefined;
  postSearchResults: SearchResult[] | undefined;
  globalProfileSearchResults: SuggestedProfileResult[] | undefined;
  fuzzyProfileSearchResults: SuggestedProfileResult[] | undefined;
  breachSearchResult: BreachSearchResult | undefined;
  socialAnalyzerResults: SocialAnalyzerResponse | undefined;
  sherlockResults: SherlockResponse | undefined;
  username: string;
  primaryPlatform: string | undefined;
  reportName: string;
  // network: TUserNetwork | undefined;
}

const initialUserSearchUiState = (
  username: string,
  primaryPlatform: string | undefined,
  reportName: string,
): UserSearchUIState => {
  return {
    profileSearchResult: undefined,
    postSearchResults: undefined,
    globalProfileSearchResults: undefined,
    fuzzyProfileSearchResults: undefined,
    breachSearchResult: undefined,
    socialAnalyzerResults: undefined,
    sherlockResults: undefined,
    username,
    primaryPlatform,
    reportName,
    // network: undefined,
  };
};

type UserSearchState = Record<string, UserSearchUIState>;

const initialState = (): UserSearchState => ({});

const getUiStateOrThrow = (
  state: UserSearchState,
  searchResultsKey: string,
) => {
  const uiState = state[searchResultsKey];
  if (!uiState) {
    throw new Error(`No state found for key ${searchResultsKey}`);
  }
  return uiState;
};

const userSearchSlice = createSlice({
  name: "user-search",
  initialState,
  reducers: {
    setProfileSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        profileSearchResult: TUserSearchHit;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.profileSearchResult = action.payload.profileSearchResult;
    },
    setPostSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        postSearchResults: SearchResult[];
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.postSearchResults = action.payload.postSearchResults;
    },
    setGlobalProfileSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        globalProfileSearchResults: SuggestedProfileResult[];
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.globalProfileSearchResults =
        action.payload.globalProfileSearchResults;
    },
    setFuzzyProfileSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        fuzzyProfileSearchResults: SuggestedProfileResult[];
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.fuzzyProfileSearchResults =
        action.payload.fuzzyProfileSearchResults;
    },
    setBreachSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        breachSearchResult: BreachSearchResult;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.breachSearchResult = action.payload.breachSearchResult;
    },
    setSocialAnalyzerResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        socialAnalyzerResults: SocialAnalyzerResponse;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.socialAnalyzerResults = action.payload.socialAnalyzerResults;
    },
    setSherlockResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        sherlockResults: SherlockResponse;
      }>,
    ) => {
      const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
      uiState.sherlockResults = action.payload.sherlockResults;
    },
    // setNetwork: (
    //   state,
    //   action: PayloadAction<{
    //     searchResultsKey: string;
    //     network: TUserNetwork;
    //   }>,
    // ) => {
    //   const uiState = getUiStateOrThrow(state, action.payload.searchResultsKey);
    //   uiState.network = action.payload.network;
    // },
    setOriginalSearchResults: (
      state,
      action: PayloadAction<{
        searchResultsKey: string;
        username: string;
        primaryPlatform: string | undefined;
        reportName: string;
      }>,
    ) => {
      const { searchResultsKey, username, primaryPlatform, reportName } =
        action.payload;

      state[searchResultsKey] = initialUserSearchUiState(
        username,
        primaryPlatform,
        reportName,
      );
    },
  },
});

export const {
  setOriginalSearchResults,
  setProfileSearchResults,
  setPostSearchResults,
  setGlobalProfileSearchResults,
  setFuzzyProfileSearchResults,
  setBreachSearchResults,
  setSocialAnalyzerResults,
  setSherlockResults,
  // setNetwork,
} = userSearchSlice.actions;

export const reducer = userSearchSlice.reducer;

export const selectReportName = (state: UserSearchUIState) => state.reportName;

export const selectProfileSearchResults = (state: UserSearchUIState) =>
  state.profileSearchResult;

export const selectUsername = (state: UserSearchUIState) => state.username;

export const selectSocialAnalyzerResults = (state: UserSearchUIState) =>
  state.socialAnalyzerResults;

// export const selectNetwork = (state: UserSearchUIState) => state.network;

export const selectGlobalProfileSearchResults = (state: UserSearchUIState) =>
  state.globalProfileSearchResults;

export const selectSearchesComplete = (state: UserSearchUIState) =>
  Object.values(state).every((value) => value !== undefined);

export const selectPostSearchResults = (state: UserSearchUIState) =>
  state.postSearchResults;
